import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { ROUTES } from '@/lib/routes';
import { formatTimeWithTimeZone } from '@/lib/utils/format-time-with-timezone';
import { SessionHub } from '@addmile/backend-client';
import { CellContext } from '@tanstack/react-table';

import { StatusSelect } from '../../StatusSelect/StatusSelect';
import { useStatusChange } from '../../StatusSelect/useStatusChange';
import Button from '../../shared/Button/Button';
import { Text } from '../../shared/Text/Text';
import { useColumnsVisibility } from './useColumnsVisibility';

export const useSessionsTableData = (sessions: SessionHub[]) => {
  const { selectedStatus, handleStatusChange } = useStatusChange(sessions);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const filteredSessions = useMemo(() => {
    if (!statusFilter) return sessions;
    return sessions.filter(
      (session) => (selectedStatus[session.id] || session.status) === statusFilter,
    );
  }, [sessions, selectedStatus, statusFilter]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: '#',
        cell: ({ row }: CellContext<SessionHub, string>) => <span>{row.index + 1}</span>,
        size: 10,
      },
      {
        accessorKey: 'startsAt',
        header: 'Date',
        cell: ({ getValue }: CellContext<SessionHub, string>) => (
          <span>
            {typeof getValue() === 'string'
              ? formatTimeWithTimeZone(getValue()).dateOnly
              : getValue()}
          </span>
        ),
        size: 100,
      },
      {
        id: 'time',
        accessorKey: 'startsAt',
        header: 'Time',
        cell: ({ getValue }: CellContext<SessionHub, string>) =>
          typeof getValue() === 'string' ? formatTimeWithTimeZone(getValue()).time : getValue(),
        size: 80,
      },
      {
        accessorKey: 'coach',
        header: 'Coach',
        cell: ({ getValue }: CellContext<SessionHub, SessionHub['coach']>) => (
          <div className="flex gap-2 items-center">
            <img
              src={getValue()?.photoUrl || '/icons/avatar.svg'}
              alt="photo"
              className="h-[36px] min-w-[36px] rounded-full object-cover"
            />
            <Text.Regular>{getValue()?.name}</Text.Regular>
          </div>
        ),
        size: 120,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 100,
        cell: ({ row }: CellContext<SessionHub, string>) => {
          const status = selectedStatus[row.original.id] || row.original.status;
          return (
            <StatusSelect
              status={status}
              onChange={(value) => handleStatusChange(row.original.id, value)}
            />
          );
        },
      },
      {
        accessorKey: 'bookedAt',
        header: 'Request Date',
        size: 100,
        cell: ({ getValue }: CellContext<SessionHub, string>) =>
          typeof getValue() === 'string' ? formatTimeWithTimeZone(getValue()).date : getValue(),
      },
      {
        accessorKey: 'user',
        header: 'User Email',
        size: 100,
        cell: ({ getValue }: CellContext<SessionHub, SessionHub['user']>) => {
          return (
            <Link
              to={`${ROUTES.ADMIN_USER_DATA}?email=${encodeURIComponent(getValue()?.email)}`}
              className="!text-primary-40"
            >
              <Text.Regular>{getValue()?.email}</Text.Regular>
            </Link>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }: CellContext<SessionHub, string>) => (
          <Button
            size="s"
            variant="transparent"
            className="w-[112px]"
            onClick={() => window.open(`/admin/sessions/${row.original.id}`, '_blank')}
            label="Details"
          />
        ),
      },
    ],
    [handleStatusChange, selectedStatus],
  );

  const { columnVisibility, toggleColumnVisibility, columnOptions } = useColumnsVisibility({
    columnsKeys: {
      id: true,
      startsAt: true,
      time: true,
      coach: true,
      status: true,
      bookedAt: true,
      user: true,
      actions: true,
    },
    columns,
  });

  return {
    data: filteredSessions,
    columns,
    statusFilter,
    setStatusFilter,
    columnVisibility,
    toggleColumnVisibility,
    columnOptions,
  };
};
